import type { CSSProperties, MutableRefObject } from 'react';
import { modalLayerZIndices } from './modal-layer-z-indices';
import {
  ModalEntryPhase,
  type ModalBackdropConfig,
  type ModalEntry,
  type ModalLayerZIndices,
  type ModalRegistration,
  type ModalStackContextValue,
  type UseRegisterModalInput,
} from './modal-stack-types';

export enum ModalVisibilityAction {
  RegisterOpen = 'registerOpen',
  MarkClosing = 'markClosing',
  Unregister = 'unregister',
}

interface ModalRegistrationSelectorInput {
  id: string;
  stack?: readonly ModalEntry[] | null;
}

interface ModalVisibilityActionInput {
  visible: boolean;
  holdUntilExitComplete: boolean;
}

interface OpenModalRegistrationInput {
  id: string;
  onCloseRef: MutableRefObject<() => void>;
  backdropRef: MutableRefObject<ModalBackdropConfig | null>;
}

export interface NormalizedUseRegisterModalInput {
  id: string;
  visible: boolean;
  onClose: () => void;
  backdrop: ModalBackdropConfig | null;
  holdUntilExitComplete: boolean;
}

export interface ModalRegistrationContextParts {
  stack?: ModalEntry[];
  registerModal?: ModalStackContextValue['registerModal'];
  markClosing?: ModalStackContextValue['markClosing'];
  unregisterModal?: ModalStackContextValue['unregisterModal'];
}

interface ModalRegistrationResultInput {
  layer: ModalLayerZIndices;
  isTopOpen: boolean;
  completeExit: () => void;
}

type BackdropRegistrationDependencies = readonly [
  opacity: number | undefined,
  blur: number | undefined,
  useGradient: boolean | undefined,
  topOffset: number | undefined,
  animationDuration: number | undefined,
  style: CSSProperties | undefined,
];

const EMPTY_MODAL_REGISTRATION_CONTEXT_PARTS: ModalRegistrationContextParts = {};
const EMPTY_BACKDROP_REGISTRATION_DEPENDENCIES: BackdropRegistrationDependencies = [
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
];

function getTopOpenEntry(stack: readonly ModalEntry[]): ModalEntry | null {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    const entry = stack[index];
    if (entry?.phase === ModalEntryPhase.Open) return entry;
  }
  return null;
}

function getModalStackLength(stack: readonly ModalEntry[] | null | undefined): number {
  if (!stack) return 0;
  return stack.length;
}

function findModalVisualIndex(input: ModalRegistrationSelectorInput): number {
  if (!input.stack) return -1;
  return input.stack.findIndex((entry) => entry.id === input.id);
}

function getEffectiveModalVisualIndex(input: {
  visualIndex: number;
  fallbackIndex: number;
}): number {
  if (input.visualIndex >= 0) return input.visualIndex;
  return input.fallbackIndex;
}

export function normalizeUseRegisterModalInput(input: UseRegisterModalInput): NormalizedUseRegisterModalInput {
  return {
    id: input.id,
    visible: input.visible,
    onClose: input.onClose,
    backdrop: input.backdrop,
    holdUntilExitComplete: input.holdUntilExitComplete ?? false,
  };
}

export function getModalRegistrationContextParts(
  context: ModalStackContextValue | null,
): ModalRegistrationContextParts {
  if (!context) return EMPTY_MODAL_REGISTRATION_CONTEXT_PARTS;
  return {
    stack: context.stack,
    registerModal: context.registerModal,
    markClosing: context.markClosing,
    unregisterModal: context.unregisterModal,
  };
}

export function isTopOpenModal(input: ModalRegistrationSelectorInput): boolean {
  if (!input.stack) return true;
  const topOpenEntry = getTopOpenEntry(input.stack);
  return topOpenEntry ? topOpenEntry.id === input.id : true;
}

export function getModalRegistrationLayer(input: ModalRegistrationSelectorInput): ModalLayerZIndices {
  const visualIndex = findModalVisualIndex(input);
  const effectiveIndex = getEffectiveModalVisualIndex({
    visualIndex,
    fallbackIndex: getModalStackLength(input.stack),
  });
  return modalLayerZIndices(effectiveIndex);
}

export function getBackdropRegistrationDependencies(
  backdrop: ModalBackdropConfig | null,
): BackdropRegistrationDependencies {
  if (!backdrop) return EMPTY_BACKDROP_REGISTRATION_DEPENDENCIES;
  return [
    backdrop.opacity,
    backdrop.blur,
    backdrop.useGradient,
    backdrop.topOffset,
    backdrop.animationDuration,
    backdrop.style,
  ];
}

export function getModalVisibilityAction(input: ModalVisibilityActionInput): ModalVisibilityAction {
  if (input.visible) return ModalVisibilityAction.RegisterOpen;
  if (input.holdUntilExitComplete) return ModalVisibilityAction.MarkClosing;
  return ModalVisibilityAction.Unregister;
}

export function createOpenModalRegistration(input: OpenModalRegistrationInput): ModalEntry {
  return {
    id: input.id,
    onClose: () => input.onCloseRef.current(),
    backdrop: input.backdropRef.current,
    phase: ModalEntryPhase.Open,
  };
}

export function createModalRegistrationResult(input: ModalRegistrationResultInput): ModalRegistration {
  return {
    contentZIndex: input.layer.content,
    underlayZIndex: input.layer.underlay,
    isTopOpen: input.isTopOpen,
    completeExit: input.completeExit,
  };
}
